import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ThemeService } from '../theme.service';

/**
 * 헤더 좌측 / `tabs` 헤더 / 사이드바 상단, 세 배치 지점이 공유하는 브랜드(로고+텍스트) 블록.
 * lib 공개 API(`index.ts`)로 export하지 않는다 — 앱은 `sbe-app-shell`의 `title`/`brandLogo`
 * 입력을 통해서만 이 컴포넌트를 간접적으로 쓴다.
 *
 * 입력 이름이 셸의 공개 입력과 같은 `title`이 아니라 `label`인 이유 — 컴포넌트 입력
 * `title`은 HTML `title` 속성(툴팁) 의미를 가려버린다.
 */
@Component({
  selector: 'sbe-shell-brand',
  imports: [NgTemplateOutlet, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (hasBrand()) {
      @if (hasHomeRoute()) {
        <a
          [routerLink]="homeRoute()"
          class="flex items-center gap-2 no-underline text-gray-800 dark:text-gray-100"
        >
          <ng-container *ngTemplateOutlet="inner" />
        </a>
      } @else {
        <span class="flex items-center gap-2 text-gray-800 dark:text-gray-100">
          <ng-container *ngTemplateOutlet="inner" />
        </span>
      }
    }
    <ng-template #inner>
      @if (activeLogo(); as src) {
        <img [src]="src" [alt]="logoAlt()" [class]="logoClass()" />
      }
      @if (label(); as text) {
        <span [class]="labelClass()">{{ text }}</span>
      }
    </ng-template>
  `,
})
export class ShellBrandComponent {
  private readonly theme = inject(ThemeService);

  readonly logo = input<string | null>(null);
  readonly logoDark = input<string | null>(null);
  readonly logoAltText = input<string | null>(null);
  readonly label = input<string | null>(null);
  readonly homeRoute = input<string | null>(null);
  readonly logoClass = input<string>('h-8 w-auto');
  /** 기본값은 툴바 좌측 배치 기준. `tabs` 헤더는 원래 `<h1 class="text-xl font-semibold">` 크기를 유지하도록 override한다. */
  readonly labelClass = input<string>('text-base font-semibold');

  protected readonly hasBrand = computed(() => !!this.logo() || !!this.label());
  /** `null`뿐 아니라 `undefined`(타입을 우회해 명시적으로 바인딩된 경우)도 안전하게 제외한다. */
  protected readonly hasHomeRoute = computed(() => this.homeRoute() !== null && this.homeRoute() !== undefined);
  protected readonly activeLogo = computed(() =>
    this.theme.mode() === 'dark' ? (this.logoDark() ?? this.logo()) : this.logo(),
  );
  /** 텍스트가 함께 보이면 로고는 장식(alt="")이다 — 스크린리더가 이름을 두 번 읽지 않게 한다. */
  protected readonly logoAlt = computed(() => (this.label() ? '' : (this.logoAltText() ?? '홈')));
}
